import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const SECTIONS = [
  { t: "1. Batch / lot number", d: "The COA must state a batch number, and that number must match the label on the vial you receive. A certificate without a batch number — or one that never changes across months of stock — is not evidence of anything." },
  { t: "2. Testing laboratory and date", d: "A genuine COA names the laboratory that performed the analysis and the date of testing. Recent dates matter: peptide suppliers restock frequently, so a COA from a year ago says nothing about today's batch." },
  { t: "3. HPLC purity result and chromatogram", d: "High-Performance Liquid Chromatography separates the sample into its components. The COA should state the purity percentage (reputable suppliers specify ≥99%) and ideally include the chromatogram itself — the graph showing one dominant peak. Multiple significant peaks indicate impurities." },
  { t: "4. Identity confirmation", d: "Purity alone doesn't prove the vial contains the right compound. Look for identity confirmation — typically mass spectrometry (MS) showing the measured molecular weight matching the compound's theoretical mass." },
  { t: "5. Quantity verification", d: "The stated fill (e.g. 10 mg) should be verified, not assumed. Under-filled vials are one of the most common problems with untested suppliers." },
];

export default function GuideReadCoa() {
  useEffect(() => {
    document.title = "How to Read a Peptide Certificate of Analysis (COA) | Peptigen Labs";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "The 5 things a genuine peptide COA must show: batch number, named lab and date, HPLC purity with chromatogram, identity confirmation by mass spectrometry, and quantity verification.");
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "How to Read a Peptide Certificate of Analysis (COA)",
    description: "The 5 things a genuine peptide COA must show, and the red flags that expose a fake one.",
    author: { "@type": "Organization", name: "Peptigen Labs Ltd" },
    publisher: { "@type": "Organization", name: "Peptigen Labs Ltd", url: "https://peptigenlabs.co.uk" },
    mainEntityOfPage: "https://peptigenlabs.co.uk/guides/how-to-read-a-peptide-coa",
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-14" data-testid="guide-read-coa">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#00C2D6] mb-2">// Buyer's guide · updated 2026</div>
      <h1 className="text-4xl sm:text-5xl font-extrabold text-[#F7FAFC] mb-4">How to Read a Peptide COA</h1>
      <p className="text-[#D5DCE8] text-base leading-relaxed mb-8">
        A Certificate of Analysis is the only objective evidence of what's actually in a vial — but only if
        you know what to look for. Here are the five things a genuine COA must show, and the red flags that
        expose a worthless one.
      </p>
      <div className="space-y-6 mb-10">
        {SECTIONS.map((s, i) => (
          <div key={i} className="p-5 bg-[#0B1220] border border-[#00C2D6]/15 rounded-sm">
            <h2 className="text-base md:text-lg font-bold text-[#F7FAFC] mb-1">{s.t}</h2>
            <p className="text-sm text-[#9CA8B8] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
      <h2 className="text-lg font-bold text-[#5EEAD4] mb-3 uppercase tracking-wider">Red flags</h2>
      <ul className="text-sm text-[#D5DCE8] leading-relaxed space-y-2 mb-10 list-disc pl-5">
        <li>No batch number, or a batch number that doesn't match the vial label</li>
        <li>No named laboratory — "tested in-house" is not independent verification</li>
        <li>A purity figure with no chromatogram or supporting data</li>
        <li>The same certificate reused across every product on the site</li>
        <li>A supplier that only shows the COA after you've paid</li>
      </ul>
      <p className="text-sm text-[#D5DCE8] leading-relaxed mb-4">
        Every Peptigen Labs product page publishes the independent COA for the current batch — you can check
        each one against this list before ordering. See also our{" "}
        <Link to="/guides/where-to-buy-peptides-uk" className="text-[#5EEAD4] underline" data-testid="guide-link-buy">UK buyer's checklist</Link>{" "}
        or the <Link to="/faq" className="text-[#5EEAD4] underline">full FAQ</Link>.
      </p>
      <p className="text-xs text-[#7B8595]">All products are supplied strictly for laboratory research use only — not for human consumption.</p>
    </div>
  );
}
